import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Select } from '@/components/common';
import { StateSelect } from '@/components/forms/AddressFields';
import { useCityLookup, useRTOLookup } from '@/hooks/useGeography';
import { usePincodeLookup } from '@/hooks/usePincodeLookup';

const PIN_CODE_REGEX = /^\d{6}$/;

/**
 * M-04 — the RTO address block Add Policy shows instead of Vehicle Number and
 * Registration Date once the broker says the vehicle is a NEW first
 * registration. A vehicle that has not been to an RTO yet has no plate and no
 * registration date to key in; what it does have is the office it is about to
 * be registered at, and that office is a commission rate dimension
 * (`rto_id`, seeded on Motor by products/migrations/_seed_helpers.py and read
 * by apps.commissions.services._motor_facts).
 *
 * Deliberately id-valued throughout, unlike `AddressFields`, whose State/City
 * carry NAMES because that is what every address column on this platform
 * stores. Here the three boxes exist only to narrow the fourth, so they are
 * the master's primary keys: `?state=` and `?city=` are what the City and RTO
 * lookups scope by, and a name would have to be matched back to a row first —
 * which is exactly where a legacy spelling resolves to nothing.
 *
 * A Fragment of four independent controls, not a wrapping <div>, so the caller
 * drops it straight into its existing `grid-cols-…` as sibling cells — the
 * same contract `AddressFields` already has.
 *
 * Nothing here is required: decision 13 keeps RTO optional, so every failure
 * mode (a PIN that resolves to nothing, a city with no offices on the master,
 * a lookup that fails) leaves the broker a form they can still save.
 *
 * Clearing follows `AddressFields`' rule: only a change the USER made clears
 * what sat under it. State/City arriving from an edit form's `reset()` is
 * hydration, not a change, and must never wipe the saved RTO — which is why
 * the cascade lives in the controls' own `onChange`, not in an effect
 * watching the values.
 */
export interface RtoAddressFieldsProps {
  /** State id. */
  state: string;
  onStateChange: (value: string) => void;
  /** City id. */
  city: string;
  onCityChange: (value: string) => void;
  pincode: string;
  onPincodeChange: (value: string) => void;
  /** RTO id — the only one of the four that is saved on the policy. */
  rto: string;
  onRtoChange: (value: string) => void;

  stateError?: string;
  cityError?: string;
  pincodeError?: string;
  rtoError?: string;

  /**
   * Labels for a saved State/City/RTO whose row has not loaded yet (edit mode
   * hydrates the ids before the option lists arrive). Without these the boxes
   * would show a broker a raw UUID for a render or two.
   */
  stateFallbackLabel?: string;
  cityFallbackLabel?: string;
  rtoFallbackLabel?: string;

  disabled?: boolean;
}

export function RtoAddressFields({
  state,
  onStateChange,
  city,
  onCityChange,
  pincode,
  onPincodeChange,
  rto,
  onRtoChange,
  stateError,
  cityError,
  pincodeError,
  rtoError,
  stateFallbackLabel,
  cityFallbackLabel,
  rtoFallbackLabel,
  disabled,
}: RtoAddressFieldsProps) {
  const { lookup, loading: pincodeLoading } = usePincodeLookup();
  const [pincodeHint, setPincodeHint] = useState<string | null>(null);
  // The last 6-digit code already reflected in State/City, so tabbing back
  // through an unchanged PIN never re-fires the lookup or re-fills a box the
  // broker has since corrected by hand. Same rule as AddressFields.
  const lastResolvedCode = useRef<string | null>(null);
  const selfInitiatedPincode = useRef<string | null>(null);

  useEffect(() => {
    const trimmed = pincode ? pincode.trim() : null;
    if (trimmed !== selfInitiatedPincode.current) {
      lastResolvedCode.current = trimmed;
    }
  }, [pincode]);

  // ── City, scoped to the chosen State ──────────────────────────────────
  // Held back entirely until a state resolves: an unscoped city lookup is
  // every city in the country, which is not a dropdown.
  const {
    data: cities,
    isLoading: citiesLoading,
    isError: citiesError,
  } = useCityLookup(state || undefined, { enabled: !!state });

  const cityOptions = useMemo(() => {
    const list = state && Array.isArray(cities) ? cities : [];
    const opts = list.map((c) => ({ value: c.id, label: c.name }));
    // Same "never drop a value on file" rule as StateSelect.
    if (city && !opts.some((o) => o.value === city)) {
      opts.unshift({ value: city, label: cityFallbackLabel || 'Selected city' });
    }
    return opts;
  }, [cities, state, city, cityFallbackLabel]);

  // ── RTO, scoped to the chosen State and (when there is one) City ───────
  //
  // Both lists, because the city narrowing has to be UNDOABLE. `RTO.city` is
  // nullable and the GoDigit master import creates its own City rows by name,
  // so a city the PIN code resolves to can easily have no office attached to
  // it — a different spelling ('Bengaluru' / 'Bangalore'), or simply a
  // non-metro with none listed. Narrowing to that city with nothing behind it
  // left the RTO select holding a single disabled placeholder, and the City
  // select has no blank row to retreat to, so the field became unfillable for
  // the rest of the form. An empty city list falls back to the State's, and
  // says so.
  const {
    data: cityRtos,
    isLoading: cityRtosLoading,
    isError: cityRtosError,
  } = useRTOLookup(state || undefined, city || undefined, { enabled: !!state && !!city });

  const {
    data: stateRtos,
    isLoading: stateRtosLoading,
    isError: stateRtosError,
  } = useRTOLookup(state || undefined, undefined, { enabled: !!state });

  const cityRtoList = useMemo(
    () => (city && Array.isArray(cityRtos) ? cityRtos : []),
    [city, cityRtos]
  );
  // A city whose own lookup is still in flight is not yet "a city with no
  // offices" — waiting avoids a flash of the state-wide list under it.
  const cityRtosPending = !!city && cityRtosLoading;
  const usingStateFallback = !!city && !cityRtosPending && cityRtoList.length === 0;
  const rtos = !city
    ? stateRtos
    : cityRtosPending
      ? undefined
      : usingStateFallback
        ? stateRtos
        : cityRtoList;
  const rtosLoading = cityRtosPending || ((!city || usingStateFallback) && stateRtosLoading);
  const rtosError = !city || usingStateFallback ? stateRtosError : cityRtosError;

  const rtoOptions = useMemo(() => {
    const list = state && Array.isArray(rtos) ? rtos : [];
    const opts = list.map((r) => ({
      value: r.id,
      label: r.name ? `${r.code} — ${r.name}` : r.code,
    }));
    if (rto && !opts.some((o) => o.value === rto)) {
      opts.unshift({ value: rto, label: rtoFallbackLabel || 'Selected RTO' });
    }
    // A real blank option, not just the placeholder: `Select` renders its
    // placeholder as a DISABLED option, so without this an optional field
    // could never be put back to unanswered once it had been auto-filled.
    // Where this exists the placeholder is dropped (see the render), so there
    // is never a second, unchoosable empty row beside it.
    if (opts.length) opts.unshift({ value: '', label: 'Not specified' });
    return opts;
  }, [rtos, state, rto, rtoFallbackLabel]);

  // Decision 13: "a pincode whose city has exactly one RTO selects it". The
  // narrowing is the CITY's, not the PIN's — a PIN only picks a city — so this
  // fires for a city chosen by hand too, which is the same fact about the
  // same city.
  //
  // Keyed on the city it fired for so it happens ONCE per city: a broker who
  // clears the auto-filled office must not have it put straight back, and RTO
  // is optional precisely so that clearing it is a legitimate answer.
  //
  // Keyed on the CITY's own list, never on the state-wide fallback above: a
  // State with one office does not make that office this city's answer.
  const autoSelectedForCity = useRef<string | null>(null);
  useEffect(() => {
    if (!city || autoSelectedForCity.current === city) return;
    if (cityRtosLoading || !Array.isArray(cityRtos)) return;
    autoSelectedForCity.current = city;
    if (cityRtos.length === 1 && !rto) {
      onRtoChange(cityRtos[0].id);
    }
  }, [city, cityRtos, cityRtosLoading, rto, onRtoChange]);

  const handleStateChange = (value: string) => {
    if (value !== state) {
      // An office in the old state is not an option in the new one, and a City
      // that survives the change is offered back under the "never drop a value
      // on file" rule as though it belonged here.
      onCityChange('');
      onRtoChange('');
      autoSelectedForCity.current = null;
    }
    onStateChange(value);
  };

  const handleCityChange = (value: string) => {
    if (value !== city) {
      onRtoChange('');
      // The new city gets its own single auto-select attempt.
      autoSelectedForCity.current = null;
    }
    onCityChange(value);
  };

  const handlePincodeBlur = async (raw: string) => {
    const code = raw.trim();
    if (!PIN_CODE_REGEX.test(code) || code === lastResolvedCode.current) return;
    lastResolvedCode.current = code;
    const outcome = await lookup(code);
    if (outcome.status !== 'found') {
      setPincodeHint(
        outcome.status === 'error'
          ? 'Could not look this PIN code up — choose the State, City and RTO yourself.'
          : 'No match for this PIN code — choose the State, City and RTO yourself.'
      );
      return;
    }
    // Only the ids are useful here: they are what the City and RTO lookups
    // scope by. A payload without them — one cached before `state` was added
    // to PincodeLookupSerializer — fills nothing rather than something wrong,
    // and says what it did know.
    if (!outcome.stateId) {
      setPincodeHint(
        `This PIN code is in ${outcome.stateName} — choose the State, City and RTO yourself.`
      );
      return;
    }
    setPincodeHint(null);
    if (outcome.stateId !== state) {
      onStateChange(outcome.stateId);
      onCityChange('');
      onRtoChange('');
      autoSelectedForCity.current = null;
    }
    if (outcome.cityId && outcome.cityId !== city) {
      onCityChange(outcome.cityId);
      onRtoChange('');
      autoSelectedForCity.current = null;
    }
  };

  const cityPlaceholder = !state
    ? 'Choose a State first'
    : citiesLoading
      ? 'Loading cities…'
      : citiesError
        ? 'Cities could not be loaded'
        : cityOptions.length === 0
          ? 'No cities found for this State'
          : 'Select city';

  const rtoPlaceholder = !state
    ? 'Choose a State first'
    : rtosLoading
      ? 'Loading RTOs…'
      : rtosError
        ? 'RTOs could not be loaded'
        : rtoOptions.length === 0
          ? 'No RTO listed for this State'
          : 'Select RTO';

  return (
    <>
      <StateSelect
        id="rto_state"
        name="rto_state"
        label="State"
        valueKey="id"
        value={state}
        onChange={handleStateChange}
        error={stateError}
        fallbackLabel={stateFallbackLabel}
        disabled={disabled}
      />
      <Select
        id="rto_city"
        name="rto_city"
        label="City"
        value={city}
        options={cityOptions}
        placeholder={cityPlaceholder}
        error={
          cityError ||
          (citiesError ? 'The cities list failed to load — choose the RTO directly.' : undefined)
        }
        onChange={(e) => handleCityChange(e.target.value)}
        disabled={disabled || !state}
      />
      <Input
        id="rto_pincode"
        name="rto_pincode"
        label="Pin Code"
        inputMode="numeric"
        maxLength={6}
        placeholder="e.g. 400001"
        value={pincode}
        error={pincodeError}
        helperText={
          pincodeLoading
            ? 'Looking up State and City…'
            : (pincodeHint ?? 'Fills State and City, and picks the RTO when the city has only one.')
        }
        onChange={(e) => {
          const next = e.target.value.replace(/\D/g, '').slice(0, 6);
          selfInitiatedPincode.current = next || null;
          onPincodeChange(next);
          if (pincodeHint) setPincodeHint(null);
        }}
        onBlur={(e) => {
          void handlePincodeBlur(e.target.value);
        }}
        disabled={disabled}
      />
      <Select
        id="rto_id"
        name="rto_id"
        label="RTO"
        value={rto}
        options={rtoOptions}
        placeholder={rtoOptions.length ? undefined : rtoPlaceholder}
        error={
          rtoError ||
          (rtosError ? 'The RTO list failed to load — this field can be left blank.' : undefined)
        }
        helperText={
          usingStateFallback && rtoOptions.length
            ? 'No office is listed for that City — showing every RTO in the State. Optional.'
            : 'Optional. A commission rate scoped to an RTO only matches a policy that records one.'
        }
        onChange={(e) => onRtoChange(e.target.value)}
        disabled={disabled || !state}
      />
    </>
  );
}
